/**
 * SVD-native cascade / selection / price engine.
 *
 * Peer of `productHelper.ts`, but for the SVD flow only: it operates purely on the SVD shapes carried
 * directly by the `Product` domain (`product.vdas` for the ordered VDAs, `product.variants` for the raw
 * variants that carry `attributeValues`) plus the SVD-native selection type (`SvdSelection[]`). It
 * **never** constructs or reads a legacy `ProductOption` — the two flows stay fully separate (see
 * `pdp-purchase-widget-solutioning.md`).
 *
 * Implements the LLD §5 algorithms (`pdp-lld.md`):
 * - only **ACTIVE** SKUs drive availability / assumed-variant (§5.1, §2.6);
 * - availability is **unidirectional** — a VDA is narrowed only by selected VDAs earlier in `.order` (§5.1);
 * - selection reconcile on change (§5.2): later VDAs re-evaluate top-down; a still-valid value is kept, a
 *   previously-selected-but-now-invalid one falls back to its first available value, the **primary swatch
 *   always re-selects** (never left unset — the "color reset"), and a previously-unselected VDA stays unset.
 *
 * There is no separate `SvdVariant` type: the SVD variant fields live on `BaseProductVariant`
 * (`attributeValues`, `price`, `id`, `status`), so we read `product.variants` directly as `BaseProductVariant[]`.
 */

import { PRODUCT_VARIANT_STATUS } from '../domains/ProductVariant/BaseProductVariant'
import { VdaType, type SvdSelection, type SvdVda } from '../domains/svd/svd-product.types'

import type Product from '../domains/Product'
import type { BaseProductVariant } from '../domains/ProductVariant'

/** Only ACTIVE SKUs drive availability / cascade / assumed-variant (LLD §5.1, §2.6). */
const getActiveVariants = (product: Product): BaseProductVariant[] =>
  ((product.variants ?? []) as BaseProductVariant[]).filter(
    variant => typeof variant === 'object' && variant !== null && variant.status === PRODUCT_VARIANT_STATUS.ACTIVE,
  )

/** VDAs ordered by `.order` (stable for ties — preserves wire order). */
const getOrderedVdas = (product: Product): SvdVda[] => [...(product.vdas ?? [])].sort((a, b) => a.order - b.order)

/** Primary swatch = the lowest-`.order` SWATCH-type VDA — client-derived, no `isPrimarySwatch` flag (LLD §5.3). */
const getPrimarySwatchKey = (product: Product): string | undefined =>
  getOrderedVdas(product).find(vda => vda.type === VdaType.SWATCH)?.key

/** True when the variant satisfies every selected `{ vdaKey, valueKey }`. */
const variantMatchesSelection = (variant: BaseProductVariant, selected: SvdSelection[]): boolean =>
  selected.every(({ vdaKey, valueKey }) => variant.attributeValues?.[vdaKey]?.key === valueKey)

/**
 * Core primitive — the ACTIVE variants matching every selected VDA value. With an empty selection every
 * active variant matches. Only the keys present in `selected` take part in filtering (unselected VDAs and
 * non-VDA attributes such as `description` are ignored), mirroring `productHelper.getVariantsForOptions`.
 */
export function getSvdVariantsForOptions(product: Product, selected: SvdSelection[]): BaseProductVariant[] {
  return getActiveVariants(product).filter(variant => variantMatchesSelection(variant, selected))
}

/**
 * Cascade narrowing (unidirectional, LLD §5.1): the value keys of `vda` still reachable given only the
 * selections on VDAs strictly EARLIER in `.order` (lower order number). VDAs that share `vda`'s order —
 * or come later — never constrain it; unselected earlier VDAs pass their full set through. Dedups.
 *
 * Precedence is the `.order` NUMBER, not wire/array position (LLD §5.1). Distinct orders are the BE
 * contract; if orders happen to tie, equal-order VDAs simply don't narrow each other (no cross-cascade)
 * — a graceful degenerate case, never wrong behavior. Kept identical in basis to `getUpdatedSvdSelection`
 * so availability and reconcile always agree.
 */
export function getAvailableValueKeysForVda(product: Product, vda: SvdVda, selected: SvdSelection[]): string[] {
  const orderByVdaKey = new Map(getOrderedVdas(product).map(v => [v.key, v.order]))
  const earlierSelections = selected.filter(s => (orderByVdaKey.get(s.vdaKey) ?? Number.POSITIVE_INFINITY) < vda.order)

  const reachable = new Set(
    getSvdVariantsForOptions(product, earlierSelections)
      .map(variant => variant.attributeValues?.[vda.key]?.key)
      .filter((key): key is string => Boolean(key)),
  )
  return vda.values.map(value => value.key).filter(key => reachable.has(key))
}

/** First available value of `vda` in display order (`.order`), given the context selections (LLD §5.1). */
const firstAvailableValueKey = (product: Product, vda: SvdVda, context: SvdSelection[]): string | undefined => {
  const available = new Set(getAvailableValueKeysForVda(product, vda, context))
  return [...vda.values].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).find(value => available.has(value.key))?.key
}

/**
 * Apply a selection on `vdaKey` (replace/append), then reconcile every VDA later in `.order` top-down
 * (LLD §5.2). Earlier VDAs and the changed VDA are left untouched (unidirectional). For each later VDA,
 * against the running context: keep a still-valid value; otherwise the primary swatch and any
 * previously-selected VDA fall back to their first available value, a previously-unselected VDA stays
 * unset, and a VDA with no available value (∅) is dropped.
 */
export function getUpdatedSvdSelection(
  product: Product,
  currentSelected: SvdSelection[],
  vdaKey: string,
  valueKey: string,
): SvdSelection[] {
  const orderedVdas = getOrderedVdas(product)
  const changedOrder = orderedVdas.find(v => v.key === vdaKey)?.order ?? Number.POSITIVE_INFINITY
  const primarySwatchKey = getPrimarySwatchKey(product)
  const wasSelected = new Set(currentSelected.map(s => s.vdaKey))

  // Running selection: current selections + the changed VDA (replace/append).
  const next = new Map(currentSelected.map(s => [s.vdaKey, s.valueKey]))
  next.set(vdaKey, valueKey)

  const contextOf = (): SvdSelection[] => [...next].map(([k, v]) => ({ vdaKey: k, valueKey: v }))

  for (const vda of orderedVdas) {
    if (vda.order <= changedOrder) continue // only VDAs later in `.order` cascade (unidirectional)
    const context = contextOf()
    const available = new Set(getAvailableValueKeysForVda(product, vda, context))
    const current = next.get(vda.key)

    if (current !== undefined && available.has(current)) {
      // still valid → keep untouched, no need to compute a fallback
      continue
    }

    const fallback = firstAvailableValueKey(product, vda, context)
    if (available.size === 0 || fallback === undefined) {
      next.delete(vda.key) // no ACTIVE SKU in context → the VDA disappears
    } else if (vda.key === primarySwatchKey || wasSelected.has(vda.key)) {
      // primary swatch always re-selects; a previously-selected VDA falls back to first available
      next.set(vda.key, fallback)
    } else {
      next.delete(vda.key) // was unselected → stay unselected
    }
  }

  const reconciled: SvdSelection[] = []
  orderedVdas.forEach(vda => {
    const valueKey = next.get(vda.key)
    if (valueKey !== undefined) reconciled.push({ vdaKey: vda.key, valueKey })
  })
  return reconciled
}

/** The matching ACTIVE variant with the lowest price — the assumed variant (LLD §5.4). */
export function getSvdVariantWithMinPrice(product: Product, selected: SvdSelection[]): BaseProductVariant | undefined {
  const variants = getSvdVariantsForOptions(product, selected)
  if (!variants.length) return undefined
  return variants.reduce((min, variant) => ((variant.price ?? Infinity) < (min.price ?? Infinity) ? variant : min))
}

/**
 * Placeholder preselection: the first *available* value (by `.order`) of each VDA, cascaded top-down so
 * the result is always backed by an ACTIVE SKU. This is intentionally NOT the full LLD §5.3 primary-swatch
 * / card explicit-vs-assumed logic — that is tracked separately as backlog.
 */
export function getInitialSvdSelection(product: Product): SvdSelection[] {
  const selection: SvdSelection[] = []
  getOrderedVdas(product).forEach(vda => {
    const valueKey = firstAvailableValueKey(product, vda, selection)
    if (valueKey) selection.push({ vdaKey: vda.key, valueKey })
  })
  return selection
}
